import 'jquery-knob/js/jquery.knob';
import { hideBootstrapTooltips } from './bootstrap';
import {
    bindDatagridDelegates,
    initDatagridBoot,
    initDatagridFilterPanel,
    initDatagridMultipleSelect,
} from './datagrid-boot';

export { initDatagridOrdering } from './datagrid-ordering';
export {
    bindDatagridDelegates,
    initDatagridBoot,
    initDatagridFilterPanel,
    initDatagridMultipleSelect,
};

declare global {
    interface Window {
        init_datagrid?: (searchUrl?: string) => void;
        init_datagrid_floated_tables?: () => void;
        destroy_datagrid_floated_tables?: () => void;
        cleanup_datagrid_floated_tables?: () => void;
        init_datagrid_boot?: () => void;
    }
}

function getFulltextSearchUrl(input: HTMLInputElement): string {
    const raw = (input.dataset.searchUrl || '').trim();
    if (!raw) {
        return '';
    }

    // {link search!} may embed the previous searchFilter in the query string after
    // the first AJAX redraw — strip it so only `data.query` drives the next search.
    try {
        const url = new URL(raw, window.location.origin);
        for (const key of [...url.searchParams.keys()]) {
            if (key.includes('searchFilter')) {
                url.searchParams.delete(key);
            }
        }
        return `${url.pathname}${url.search}`;
    } catch {
        return raw;
    }
}

function doneTyping(input: HTMLInputElement): void {
    const $ = window.jQuery;
    const url = getFulltextSearchUrl(input);
    if (!url || !$) {
        return;
    }
    $.nette.ajax({ url, data: { query: input.value } });
    hideBootstrapTooltips();
}

/**
 * Vertical scroll host for floatThead.
 * Admin → `.nais-main`; side dialog → `.nais-sd-container` or form `__body`.
 */
function getFloatTheadScrollContainer($table: JQuery): JQuery {
    const $sd = $table.closest('.nais-sd-container');
    if ($sd.length) {
        const $body = $table.closest('.nais-sd-panel__body');
        if ($body.length) {
            const overflowY = window.getComputedStyle($body[0] as HTMLElement).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return $body;
            }
        }
        return $sd;
    }

    return $table.closest('.nais-main');
}

/**
 * Y-scroll is on `.nais-main` / SD, but visible width + X-scroll live on
 * `.nais-datagrid-panel__table`. Clamp the clone to that wrapper and keep it
 * pinned to the wrap viewport — floatThead otherwise follows the scrolled
 * table's offsetLeft, so thead looks cut off (white gap + early radius).
 */
function bindFloatTheadViewport($table: JQuery, $scroll: JQuery, $fth: JQuery): void {
    const $ = window.jQuery;
    const $wrap = $table.closest('.nais-datagrid-panel__table');
    if (!$wrap.length || !$fth.length) {
        return;
    }

    let uid = $table.data('nais-fth-uid') as string | undefined;
    if (!uid) {
        uid = `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
        $table.data('nais-fth-uid', uid);
    }
    const ns = `.naisFth_${uid}`;

    const sync = (): void => {
        const wrapEl = $wrap[0] as HTMLElement;
        const fthEl = $fth[0] as HTMLElement;
        const tableEl = $table[0] as HTMLElement;
        if (!wrapEl || !fthEl || !tableEl) {
            return;
        }

        const width = wrapEl.clientWidth;
        if (width <= 0) {
            return;
        }

        const wrapRect = wrapEl.getBoundingClientRect();
        const tableWidth = Math.max(
            tableEl.scrollWidth,
            tableEl.offsetWidth,
            $table.outerWidth() || 0,
            wrapEl.scrollWidth,
        );

        // Explicit px — floatThead may overwrite with scrollContainer (nais-main) width.
        fthEl.style.setProperty('width', `${width}px`, 'important');
        fthEl.style.setProperty('max-width', `${width}px`, 'important');
        fthEl.style.left = '0px';

        const fthTable = fthEl.querySelector('table');
        if (fthTable) {
            // Beat `width: 100% !important` on .nais-datagrid so clone spans full scroll width.
            fthTable.style.setProperty('width', `${tableWidth}px`, 'important');
            fthTable.style.setProperty('min-width', `${tableWidth}px`, 'important');
        }

        // floatThead sets translateX from the table's offset (moves with X-scroll).
        // Re-pin X to the visible wrap; keep floatThead's translateY.
        const transform = fthEl.style.transform || '';
        if (transform.includes('translate')) {
            const yMatch = /translateY\(([^)]+)\)/.exec(transform);
            const translateY = yMatch?.[1] ?? '0px';
            fthEl.style.transform = `translateX(${Math.round(wrapRect.left)}px) translateY(${translateY})`;
        }

        if (fthEl.scrollLeft !== wrapEl.scrollLeft) {
            fthEl.scrollLeft = wrapEl.scrollLeft;
        }
    };

    $table.off(ns).on(`reflowed${ns}`, sync);
    $table.off(`floatThead${ns}`).on(`floatThead${ns}`, () => {
        window.requestAnimationFrame(sync);
    });
    $wrap.off(ns).on(`scroll${ns}`, sync);
    $scroll.off(ns).on(`scroll${ns}`, () => {
        window.requestAnimationFrame(sync);
    });
    $(window).off(`resize${ns}`).on(`resize${ns}`, sync);

    const prevObserver = $table.data('nais-fth-ro') as ResizeObserver | undefined;
    prevObserver?.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => sync());
        ro.observe($wrap[0] as HTMLElement);
        $table.data('nais-fth-ro', ro);
    }

    sync();
}

/** After AJAX snippet replace, layout may not be final — first floatThead measure is often short. */
function scheduleFloatTheadReflow($table: JQuery): void {
    const run = (): void => {
        if (!$table.data('floatThead-attached') || !$table.closest('body').length) {
            return;
        }
        try {
            $table.floatThead('reflow');
        } catch {
            // table may already be detached
        }
        const $scroll = getFloatTheadScrollContainer($table);
        const $fth = ensureFloatTheadOnScrollHost($table, $scroll);
        if ($fth.length && $scroll.length) {
            bindFloatTheadViewport($table, $scroll, $fth);
        }
    };

    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(run);
    });
    window.setTimeout(run, 50);
    window.setTimeout(run, 200);
}

function findFloatTheadContainer($table: JQuery): JQuery {
    const $stored = $table.data('nais-fth-container') as JQuery | undefined;
    if ($stored?.length && $stored.closest('body').length) {
        return $stored;
    }
    const $prev = $table.prev('.floatThead-container');
    if ($prev.length) {
        return $prev;
    }
    return $table.siblings('.floatThead-container').first();
}

/** Keep the clone on the Y-scroll host (not inside the X-scroll wrap). */
function ensureFloatTheadOnScrollHost($table: JQuery, $scroll: JQuery): JQuery {
    const $fth = findFloatTheadContainer($table);
    if ($fth.length && $scroll.length && $fth.parent()[0] !== $scroll[0]) {
        $scroll.append($fth);
    }
    if ($fth.length) {
        $table.data('nais-fth-container', $fth);
        const boundUid = $table.data('nais-fth-uid') as string | undefined;
        if (boundUid) {
            $fth.attr('data-nais-fth-uid', boundUid);
        }
    }
    return $fth;
}

function unbindFloatTheadViewport($table: JQuery, $scroll?: JQuery): void {
    const $ = window.jQuery;
    const uid = $table.data('nais-fth-uid') as string | undefined;
    if (!uid) {
        return;
    }
    const ns = `.naisFth_${uid}`;
    $table.off(ns);
    $table.off(`floatThead${ns}`);
    $table.closest('.nais-datagrid-panel__table').off(ns);
    ($scroll ?? getFloatTheadScrollContainer($table)).off(ns);
    $(window).off(`resize${ns}`);
    const ro = $table.data('nais-fth-ro') as ResizeObserver | undefined;
    ro?.disconnect();
    $table.removeData('nais-fth-ro');
}

/**
 * Destroy floatThead for one table and remove its reparented clone.
 * Snippet redraws (refreshDatagrid) remove the <table> without calling this —
 * use cleanupOrphanFloatTheadContainers() in that case.
 */
export function destroyFloatedTable($table: JQuery): void {
    const $ = window.jQuery;
    if (!$?.fn?.floatThead || $table.hasClass('floatThead-table')) {
        return;
    }

    const $scroll = getFloatTheadScrollContainer($table);
    const $fth = findFloatTheadContainer($table);
    unbindFloatTheadViewport($table, $scroll);

    if ($table.data('floatThead-attached')) {
        try {
            $table.floatThead('destroy');
        } catch {
            // table may already be detached
        }
    }

    $fth.remove();
    $table.prev('.floatThead-container').remove();
    $table.removeData('nais-fth-container');
    $table.removeData('nais-fth-uid');
}

/**
 * After Nette replaces the table snippet / full navigation, the clone we moved
 * onto `.nais-main` / SD scroll host can remain → sticky thead on the next page.
 */
export function cleanupOrphanFloatTheadContainers(): void {
    const $ = window.jQuery;
    const $liveTables = $('table.floatedTable, table.nais-datagrid').filter(function filterLive() {
        return !$(this).hasClass('floatThead-table');
    });

    // No live grid → strip every clone in the shell (e.g. left after leaving a list).
    if (!$liveTables.length) {
        $('.nais-main .floatThead-container, .nais-sd-container .floatThead-container, .nais-sd-panel__body .floatThead-container, body > .floatThead-container')
            .each(function eachStray() {
                const uid = $(this).attr('data-nais-fth-uid');
                if (uid) {
                    $(window).off(`resize.naisFth_${uid}`);
                }
            })
            .remove();
        return;
    }

    $('.floatThead-container').each(function eachOrphanFth() {
        const el = this as HTMLElement;
        const $fth = $(el);
        let linked = false;

        $liveTables.each(function eachTable() {
            const $stored = $(this).data('nais-fth-container') as JQuery | undefined;
            if ($stored?.length && $stored[0] === el) {
                linked = true;
                return false;
            }
            return undefined;
        });

        if (linked) {
            return;
        }

        const uid = $fth.attr('data-nais-fth-uid');
        if (uid) {
            $(window).off(`resize.naisFth_${uid}`);
        }
        $fth.remove();
    });
}

/**
 * Skip grids in inactive Bootstrap tabs / display:none — otherwise floatThead
 * clones stick over the visible tab (e.g. Users preview → emails grid).
 */
function isFloatTheadEligible($table: JQuery): boolean {
    if ($table.hasClass('floatThead-table')) {
        return false;
    }
    if ($table.closest('.tab-pane:not(.show)').length > 0) {
        return false;
    }
    if (!$table.is(':visible')) {
        return false;
    }
    return true;
}

/** Bind once: destroy clones before leaving the document (full nav / bfcache). */
let floatTheadNavGuardsBound = false;

export function bindFloatTheadNavigationGuards(): void {
    const $ = window.jQuery;
    if (floatTheadNavGuardsBound || !$) {
        return;
    }
    floatTheadNavGuardsBound = true;

    window.addEventListener('pagehide', () => {
        destroyAllFloatedTables();
    });

    // Full-page links (preview, menu, …) — nette.ajax `before` does not run.
    $(document).on('click.naisFthNav', 'a[href]', function onNavClick(event) {
        const $a = $(this);
        const href = ($a.attr('href') || '').trim();
        if (
            !href
            || href.startsWith('#')
            || href.startsWith('javascript:')
            || $a.is('.ajax, .nais-side-dialog')
            || $a.attr('target') === '_blank'
            || event.defaultPrevented
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
        ) {
            return;
        }
        destroyAllFloatedTables();
    });

    // Preview / form tabs: tear down when hiding, init when shown.
    $(document).on('hide.bs.tab.naisFth', '[data-bs-toggle="tab"]', function onTabHide(event) {
        const target = $(event.target).attr('data-bs-target') || $(event.target).attr('href');
        if (!target || !target.startsWith('#')) {
            return;
        }
        const targetPane = document.getElementById(target.slice(1));
        if (!targetPane) {
            return;
        }
        $(targetPane).find('table.floatedTable, table.nais-datagrid').each(function eachHiddenGrid() {
            destroyFloatedTable($(this));
        });
    });
    $(document).on('shown.bs.tab.naisFth', '[data-bs-toggle="tab"]', () => {
        initFloatedTables();
    });
}

/**
 * Firefox-only classic (always visible) H-scrollbar for datagrid panels.
 * Overlay native bars cannot stay on via CSS — paint our own under the table.
 * Kept in this file (no separate module) by design.
 */
const FX_SCROLL_CLASS = 'nais-datagrid-fx-scroll';
const PANEL_FX_CLASS = 'nais-datagrid-panel--fx-scroll';

function isFirefoxBrowser(): boolean {
    return typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
}

function syncDatagridFxScrollBar(wrap: HTMLElement, bar: HTMLElement): void {
    const track = bar.querySelector<HTMLElement>('.nais-datagrid-fx-scroll__track');
    const thumb = bar.querySelector<HTMLElement>('.nais-datagrid-fx-scroll__thumb');
    if (!track || !thumb) {
        return;
    }

    const maxScroll = wrap.scrollWidth - wrap.clientWidth;
    const needed = maxScroll > 1;
    bar.hidden = !needed;
    wrap.closest('.nais-datagrid-panel')?.classList.toggle(PANEL_FX_CLASS, needed);
    if (!needed) {
        return;
    }

    const trackWidth = track.clientWidth;
    const thumbWidth = Math.max(32, Math.round(trackWidth * (wrap.clientWidth / wrap.scrollWidth)));
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const thumbLeft = maxScroll > 0
        ? Math.round((wrap.scrollLeft / maxScroll) * maxThumbLeft)
        : 0;
    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbLeft}px)`;
}

function bindDatagridFxScrollBar(wrap: HTMLElement, bar: HTMLElement): void {
    const track = bar.querySelector<HTMLElement>('.nais-datagrid-fx-scroll__track');
    const thumb = bar.querySelector<HTMLElement>('.nais-datagrid-fx-scroll__thumb');
    if (!track || !thumb || bar.dataset.naisFxBound === '1') {
        return;
    }
    bar.dataset.naisFxBound = '1';

    wrap.addEventListener('scroll', () => syncDatagridFxScrollBar(wrap, bar), { passive: true });

    let dragging = false;
    let startX = 0;
    let startScroll = 0;

    const onMove = (event: PointerEvent): void => {
        if (!dragging) {
            return;
        }
        const maxScroll = wrap.scrollWidth - wrap.clientWidth;
        const maxThumbLeft = Math.max(0, track.clientWidth - thumb.offsetWidth);
        if (maxScroll <= 0 || maxThumbLeft <= 0) {
            return;
        }
        wrap.scrollLeft = Math.max(
            0,
            Math.min(maxScroll, startScroll + ((event.clientX - startX) / maxThumbLeft) * maxScroll),
        );
    };

    const onUp = (): void => {
        if (!dragging) {
            return;
        }
        dragging = false;
        thumb.classList.remove('is-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
    };

    thumb.addEventListener('pointerdown', (event: PointerEvent) => {
        event.preventDefault();
        dragging = true;
        startX = event.clientX;
        startScroll = wrap.scrollLeft;
        thumb.classList.add('is-dragging');
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    });

    track.addEventListener('pointerdown', (event: PointerEvent) => {
        if (event.target === thumb || thumb.contains(event.target as Node)) {
            return;
        }
        const maxScroll = wrap.scrollWidth - wrap.clientWidth;
        const maxThumbLeft = Math.max(0, track.clientWidth - thumb.offsetWidth);
        if (maxScroll <= 0 || track.clientWidth <= 0) {
            return;
        }
        const rect = track.getBoundingClientRect();
        const clickX = event.clientX - rect.left - thumb.offsetWidth / 2;
        wrap.scrollLeft = Math.max(0, Math.min(1, clickX / maxThumbLeft)) * maxScroll;
        syncDatagridFxScrollBar(wrap, bar);
    });
}

function ensureDatagridFxScrollBar(panel: HTMLElement, wrap: HTMLElement): HTMLElement {
    let bar = panel.querySelector<HTMLElement>(`:scope > .${FX_SCROLL_CLASS}`);
    if (!bar) {
        bar = document.createElement('div');
        bar.className = FX_SCROLL_CLASS;
        bar.setAttribute('aria-hidden', 'true');
        bar.innerHTML = '<div class="nais-datagrid-fx-scroll__track">'
            + '<div class="nais-datagrid-fx-scroll__thumb"></div></div>';
        wrap.after(bar);
        bindDatagridFxScrollBar(wrap, bar);
    }

    if (wrap.dataset.naisFxResize !== '1') {
        wrap.dataset.naisFxResize = '1';
        const ro = new ResizeObserver(() => syncDatagridFxScrollBar(wrap, bar as HTMLElement));
        ro.observe(wrap);
        const table = wrap.querySelector('table');
        if (table) {
            ro.observe(table);
        }
    }

    return bar;
}

function initDatagridFirefoxScrollbars(root: ParentNode | Document = document): void {
    if (!isFirefoxBrowser()) {
        return;
    }
    root.querySelectorAll<HTMLElement>('.nais-datagrid-panel').forEach((panel) => {
        const wrap = panel.querySelector<HTMLElement>(':scope > .nais-datagrid-panel__table');
        if (!wrap) {
            return;
        }
        const bar = ensureDatagridFxScrollBar(panel, wrap);
        syncDatagridFxScrollBar(wrap, bar);
        requestAnimationFrame(() => syncDatagridFxScrollBar(wrap, bar));
    });
}

function destroyDatagridFirefoxScrollbars(root: ParentNode | Document = document): void {
    if (!isFirefoxBrowser()) {
        return;
    }
    root.querySelectorAll<HTMLElement>('.nais-datagrid-panel').forEach((panel) => {
        panel.classList.remove(PANEL_FX_CLASS);
        panel.querySelectorAll(`:scope > .${FX_SCROLL_CLASS}`).forEach((el) => el.remove());
        const wrap = panel.querySelector<HTMLElement>(':scope > .nais-datagrid-panel__table');
        if (wrap) {
            delete wrap.dataset.naisFxResize;
        }
    });
}

/**
 * Admin grids: position:fixed + scrollContainer (never absolute — that wraps
 * the scroll host and collapses the shell / side dialog).
 * Clone is reparented onto the scroll host so panel `overflow: clip` and the
 * table's overflow-x wrapper do not clip / mis-size it.
 */
export function initFloatedTables(): void {
    const $ = window.jQuery;
    if (!$?.fn?.floatThead) {
        initDatagridFirefoxScrollbars();
        return;
    }

    // refreshDatagrid / filter redraw replaces the table; drop orphaned clones first
    cleanupOrphanFloatTheadContainers();

    $('table.floatedTable, table.nais-datagrid').each(function eachFloatedTable() {
        const $table = $(this);
        if ($table.hasClass('floatThead-table')) {
            return;
        }

        // Hidden tab (Users preview emails, …): never leave a floating clone around.
        if (!isFloatTheadEligible($table)) {
            if ($table.data('floatThead-attached')) {
                destroyFloatedTable($table);
            }
            return;
        }

        const $scroll = getFloatTheadScrollContainer($table);
        const inShell = $table.closest('.nais-main, .nais-sd-window, .nais-sd-container').length > 0;

        if ($table.data('floatThead-attached')) {
            $table.floatThead('reflow');
            if (inShell && $scroll.length) {
                const $existing = findFloatTheadContainer($table);
                if ($existing.length) {
                    bindFloatTheadViewport($table, $scroll, $existing);
                }
            }
            scheduleFloatTheadReflow($table);
            return;
        }

        const options: Record<string, unknown> = {
            position: $scroll.length && inShell ? 'fixed' : 'absolute',
            autoReflow: true,
            zIndex: $table.closest('.nais-sd-window, .nais-sd-container').length ? 30 : 20,
        };

        if ($scroll.length && inShell) {
            options.scrollContainer = () => $scroll;
        }

        $table.floatThead(options);

        if (!inShell || !$scroll.length) {
            return;
        }

        // Plugin inserts clone before the table (inside overflow wrappers / clipped panel).
        const $fth = ensureFloatTheadOnScrollHost($table, $scroll);
        if ($fth.length) {
            bindFloatTheadViewport($table, $scroll, $fth);
        }
        // Pagination / filter AJAX: first measure often runs before wide-table layout settles.
        scheduleFloatTheadReflow($table);
    });

    initDatagridFirefoxScrollbars();
}

/** Destroy every admin/SD floated header (e.g. before a full table snippet replace). */
export function destroyAllFloatedTables(): void {
    const $ = window.jQuery;
    destroyDatagridFirefoxScrollbars();
    if (!$?.fn?.floatThead) {
        return;
    }
    $('table.floatedTable, table.nais-datagrid').each(function eachDestroy() {
        destroyFloatedTable($(this));
    });
    cleanupOrphanFloatTheadContainers();
}

export function initDatagrid(searchUrl?: string): void {
    const $ = window.jQuery;
    const inputs = document.querySelectorAll<HTMLInputElement>('.nais-datagrid-search__input');
    inputs.forEach((input) => {
        if (searchUrl && !input.dataset.searchUrl) {
            input.dataset.searchUrl = searchUrl;
        }

        const field = input.closest('.nais-datagrid-search__field');
        if (!$ || !field) {
            return;
        }

        // Resolve URL at click/enter time — afterAjax may re-init without a URL argument.
        $(input).off('keypress.naisDg').on('keypress.naisDg', (event) => {
            if (event.key === 'Enter' || event.keyCode === 13) {
                event.preventDefault();
                doneTyping(input);
            }
        });
        $(field).find('button.search').off('click.naisDg').on('click.naisDg', () => doneTyping(input));

        const cancelUrl = (input.dataset.searchCancelUrl || '').trim();
        if (cancelUrl) {
            $(field)
                .find('.nais-datagrid-search__clear')
                .off('click.naisDg')
                .on('click.naisDg', () => {
                    $.nette.ajax({ url: cancelUrl });
                });
        }
    });

    initFloatedTables();
    initDatagridBoot();
    initDatagridFirefoxScrollbars();
}

window.init_datagrid = initDatagrid;
window.init_datagrid_floated_tables = initFloatedTables;
window.destroy_datagrid_floated_tables = destroyAllFloatedTables;
window.cleanup_datagrid_floated_tables = cleanupOrphanFloatTheadContainers;
window.init_datagrid_boot = initDatagridBoot;

/** Compact Select2 for multiselect fields inside the datagrid filter panel. */
export function initDatagridFilterSelects(root: ParentNode | Document = document): void {
    const $ = window.jQuery;
    if (!$?.fn?.select2) {
        return;
    }

    const scope = root === document ? $(document) : $(root);
    scope.find('.nais-datagrid-filter-panel select[multiple]:not(.select2-ignore)').each(function eachFilterMultiSelect() {
        const $el = $(this);
        if ($el.data('select2')) {
            return;
        }

        $el.select2({
            width: '100%',
            dropdownParent: $el.closest('.nais-datagrid-filter-panel'),
        });
    });
}

window.init_datagrid_filter_selects = initDatagridFilterSelects;

/** Native rows-per-page select — exclude from global Select2 (class select2-ignore). */
export function initDatagridRowsPerPageSelect(root: ParentNode | Document = document): void {
    const $ = window.jQuery;
    const scope = root === document ? $(document) : $(root);
    scope.find('select.rowsOnPageSelect').each(function eachRowsSelect() {
        const el = this as HTMLSelectElement;
        el.classList.add('select2-ignore');
        if ($?.fn?.select2) {
            const $el = $(el);
            if ($el.data('select2')) {
                $el.select2('destroy');
            }
        }
    });
}

export function initDatagridConfirmDialogs(): void {
    const $ = window.jQuery;
    $(document).off('click.confirmDialog').on('click.confirmDialog', '.row-actions a, .confirmDialog', function (e) {
        // jQuery UI rebinds `this` inside buttons[].click() to the dialog itself; capture the trigger now.
        const $trigger = $(this);
        const dialogText = $trigger.data('dialog-text');
        const dialogTitleText = $trigger.data('dialog-title');
        if (!dialogText || !dialogTitleText) return;
        const d = document.getElementById('dictionary');
        const dictionary = JSON.parse(d?.textContent || d?.innerHTML || '{}');
        e.preventDefault();
        $('<div></div>').dialog({
            resizable: false,
            height: 'auto',
            width: 400,
            modal: true,
            title: dialogTitleText,
            open() { $(this).html(dialogText); },
            buttons: [
                {
                    text: dictionary.yes,
                    click() {
                        if ($trigger.hasClass('ajax-confirmDialog')) {
                            $.nette.ajax({ url: $trigger.attr('href') });
                        } else {
                            window.location.href = $trigger.attr('href');
                        }
                        $(this).dialog('close');
                    },
                },
                { text: dictionary.no, click() { $(this).dialog('close'); } },
            ],
        });
    });
}
